import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel"

import { CardImage } from "@/components/brand-kit/card-image"
import { alternateCardImage } from "@/lib/card-image"
import Link from "next/link"
import { RecommendedProduct, isFilterProductType, compareByIllustratedThenPriced } from "@/lib/recommendations"
import { formatProductTitle } from "@/lib/breadcrumb-utils"
import { BadgeList } from "@/components/badges/badge-list"
import { badgesFromRecommended } from "@/components/badges/from-product"
import { productCardCtaName } from "@/lib/card-link-names"
import { dealCtaLabel, hasRenderedPrice } from "@/lib/deal-cta"

// Extracted to avoid re-creating objects on every render

interface BestsellersBlockProps {
  products: RecommendedProduct[]
}

export function BestsellersBlock({ products }: BestsellersBlockProps) {
  const filtered = (products || []).filter(p => !isFilterProductType(p.type || ''))
  if (filtered.length === 0) {
    return null
  }

  // Illustrated first, then priced — the one rail rule, from
  // lib/recommendations.ts. This block used to carry its own copy of the
  // price-only comparator and re-sorted what the selector had already
  // ordered, which put un-illustrated cards back at the front (FRIDGE-398).
  const sortedProducts = [...filtered].sort(compareByIllustratedThenPriced)

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="pt-4 pb-1 px-4 sm:px-5">
        <h3 className="text-base sm:text-lg font-bold text-gray-900 line-clamp-2">🏆 Bestsellers on Fridge.com</h3>
        <p className="text-xs text-gray-600 mt-1 mb-2">Most viewed products across all categories</p>
      </div>
      <div className="px-2 sm:px-4 pb-4">
        <Carousel
          opts={{
            align: "start",
            loop: false,
            slidesToScroll: 1,
            dragFree: true,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-1 sm:-ml-1.5">
            {sortedProducts.map((product) => {
              // FRIDGE-429 — ONE boolean gates the price row AND picks the CTA label.
              const showsPrice = hasRenderedPrice(product.price)
              return (
              <CarouselItem key={product.id} className="pl-1 sm:pl-1.5 basis-[280px] sm:basis-1/2 md:basis-1/3 lg:basis-1/6">
                <div data-product-image-card className="border rounded-lg p-2.5 bg-white h-full flex flex-col hover:shadow-md transition-shadow">
                <div className="relative">
                  {/* Canonical badge set — persists this product's badges
                      (Price Drop, Energy Star, etc.) identically sitewide.
                      Block carousel → top-priority badge only (spec §5). */}
                  <BadgeList badges={badgesFromRecommended(product)} variant="carousel" placement="overlay-tl" />
                  <div className="aspect-square bg-white rounded-lg overflow-hidden">
                    <CardImage src={product.images?.[0] || '/placeholder.svg'} alternate={alternateCardImage(product.images?.[0] || '', product.images || [])} href={product.canonical_url || `/products/${product.id}`} alt={`${product.title} | Fridge.com`} width={150} height={150} />
                  </div>
                </div>
                <div className="text-xs text-[#0469f0] font-medium mb-1">Most Popular</div>
                <Link href={product.canonical_url || `/products/${product.id}`} className="text-sm font-medium mb-1 line-clamp-2 min-h-[2.5rem] hover:underline">
                  {formatProductTitle(product.title)}
                </Link>
                <div className="mt-auto space-y-2">
                  {showsPrice && typeof product.price === 'number' ? (
                    <div className="flex items-baseline gap-1.5">
                      <span className={`text-sm font-bold ${typeof product.originalPrice === 'number' && product.originalPrice > product.price ? 'text-green-600' : ''}`}>{`$${product.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                      {typeof product.originalPrice === 'number' && product.originalPrice > product.price && (
                        <span className="text-xs text-red-500 line-through">{`$${product.originalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                      )}
                    </div>
                  ) : null}
                  <Link href={product.canonical_url || `/products/${product.id}`} aria-label={productCardCtaName(dealCtaLabel(showsPrice), product, product.canonical_url || `/products/${product.id}`)}>
                    <button className="w-full h-9 text-xs font-semibold whitespace-nowrap min-h-[44px] bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] rounded-full transition-colors">
                      {dealCtaLabel(showsPrice)}
                    </button>
                  </Link>
                </div>
              </div>
            </CarouselItem>
            )
          })}
          </CarouselContent>
        </Carousel>
      </div>
    </div>
  )
}

